import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { dealingDesk, type ManualHedgeBody } from "@/api/dealingDesk";
import { ApiError } from "@/api/http";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { useWsSubscribe } from "@/ws/WebSocketProvider";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

/**
 * Dealing Desk — broker-operator console.
 *
 * Three panels:
 *  1. Net exposure board (per symbol).
 *  2. Pending hedge approvals (MANUAL queue) + recent hedge history.
 *  3. LP health strip.
 *
 * Refreshes every 5s. Approve/reject mutations invalidate the queue.
 */
export function DealingDeskPage() {
  const qc = useQueryClient();
  const exposure = useQuery({
    queryKey: ["dealing-desk", "exposure"],
    queryFn: () => dealingDesk.exposure(),
    refetchInterval: 5000,
  });
  const pending = useQuery({
    queryKey: ["dealing-desk", "pending"],
    queryFn: () => dealingDesk.pendingHedges(),
    refetchInterval: 5000,
  });
  const recent = useQuery({
    queryKey: ["dealing-desk", "recent"],
    queryFn: () => dealingDesk.recentHedges(50),
    refetchInterval: 5000,
  });
  const lps = useQuery({
    queryKey: ["dealing-desk", "lps"],
    queryFn: () => dealingDesk.lpHealth(),
    refetchInterval: 5000,
  });

  // Live WS — invalidate immediately on broker-side events instead of
  // waiting for the 5s poll. The polls remain as a fallback in case a
  // WS frame is missed.
  useWsSubscribe("fill", () => qc.invalidateQueries({ queryKey: ["dealing-desk"] }));
  useWsSubscribe("internal_fill", () => qc.invalidateQueries({ queryKey: ["dealing-desk"] }));
  useWsSubscribe("margin_event", () => qc.invalidateQueries({ queryKey: ["dealing-desk"] }));

  const [fireOpen, setFireOpen] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Dealing desk</h1>
        <Button onClick={() => setFireOpen(true)}>Fire hedge</Button>
      </div>

      <ExposureBoard rows={exposure.data ?? []} loading={exposure.isLoading} />
      <PendingHedges rows={pending.data ?? []} loading={pending.isLoading} />
      <LpHealth rows={lps.data ?? []} loading={lps.isLoading} />
      <RecentHedges rows={recent.data ?? []} loading={recent.isLoading} />

      {fireOpen && <FireHedgeModal onClose={() => setFireOpen(false)} />}
    </div>
  );
}


function FireHedgeModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<ManualHedgeBody>({
    symbol: "EUR/USD",
    side: "BUY",
    qty: "1",
  });
  const [err, setErr] = useState<string | null>(null);

  const fire = useMutation({
    mutationFn: () => dealingDesk.fireHedge(form),
    onSuccess: (h) => {
      toast.push(`Hedge ${h.side} ${h.qty} ${h.symbol} fired`, "success");
      qc.invalidateQueries({ queryKey: ["dealing-desk"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title="Fire manual hedge">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          fire.mutate();
        }}
        className="space-y-3"
      >
        <p className="text-2xs text-term-dim">
          Submits an LP order directly. The hedge_orders row is written
          with status APPROVED + mode MANUAL; the supervisor's adapter
          dispatches to the chosen LP, and the reconciler ticks
          exposure on the fill.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Symbol</Label>
            <Input
              value={form.symbol}
              onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
              required
            />
          </div>
          <div>
            <Label>Side</Label>
            <Select
              value={form.side}
              onChange={(e) => setForm({ ...form, side: e.target.value as "BUY" | "SELL" })}
            >
              <option value="BUY">BUY</option>
              <option value="SELL">SELL</option>
            </Select>
          </div>
          <div>
            <Label>Qty</Label>
            <Input
              value={form.qty}
              onChange={(e) => setForm({ ...form, qty: e.target.value })}
              required
            />
          </div>
          <div>
            <Label>LP session id (optional)</Label>
            <Input
              value={form.lp_session_id ?? ""}
              onChange={(e) => setForm({ ...form, lp_session_id: e.target.value || undefined })}
              placeholder="leave blank to auto-pick"
            />
          </div>
          <div className="col-span-2">
            <Label>Note (optional)</Label>
            <Input
              value={form.note ?? ""}
              onChange={(e) => setForm({ ...form, note: e.target.value || undefined })}
              placeholder="reason / context"
            />
          </div>
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={fire.isPending}>Fire</Button>
        </div>
      </form>
    </Modal>
  );
}

function ExposureBoard({
  rows,
  loading,
}: {
  rows: Awaited<ReturnType<typeof dealingDesk.exposure>>;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Net exposure</span>
          <span className="text-2xs uppercase tracking-wider text-term-mute">live</span>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {loading ? (
          <div className="p-3 text-sm text-term-mute">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="Flat" description="No net exposure across any symbol." />
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-term-elev text-term-mute">
              <tr>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Symbol</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Net qty</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Avg price</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Side</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const n = Number(r.net_qty);
                const isLong = n > 0;
                const isFlat = n === 0;
                return (
                  <tr key={r.symbol} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-term-fg">{r.symbol}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">{r.net_qty}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{r.avg_price}</td>
                    <td className="px-3 py-1.5 text-right">
                      {isFlat ? (
                        <Badge tone="neutral">FLAT</Badge>
                      ) : isLong ? (
                        <Badge tone="good">LONG</Badge>
                      ) : (
                        <Badge tone="warn">SHORT</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function PendingHedges({
  rows,
  loading,
}: {
  rows: Awaited<ReturnType<typeof dealingDesk.pendingHedges>>;
  loading: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const approve = useMutation({
    mutationFn: (id: string) => dealingDesk.approveHedge(id),
    onSuccess: () => {
      toast.push("Hedge approved", "success");
      qc.invalidateQueries({ queryKey: ["dealing-desk"] });
    },
    onError: (e) => toast.push(e instanceof ApiError ? e.message : String(e), "error"),
  });
  const reject = useMutation({
    mutationFn: (id: string) => dealingDesk.rejectHedge(id),
    onSuccess: () => {
      toast.push("Hedge rejected", "warn");
      qc.invalidateQueries({ queryKey: ["dealing-desk"] });
    },
    onError: (e) => toast.push(e instanceof ApiError ? e.message : String(e), "error"),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Pending hedge approvals</span>
          <Badge tone={rows.length > 0 ? "warn" : "neutral"}>{rows.length}</Badge>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {loading ? (
          <div className="p-3 text-sm text-term-mute">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="Queue empty"
            description="No MANUAL-mode hedge orders waiting on a dealer."
          />
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-term-elev text-term-mute">
              <tr>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Symbol</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Side</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Qty</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Net before</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Threshold</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Reason</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((h) => (
                <tr key={h.id} className="border-t border-term-border hover:bg-term-elev">
                  <td className="px-3 py-1.5 font-mono text-term-fg">{h.symbol}</td>
                  <td className="px-3 py-1.5">
                    <Badge tone={h.side === "BUY" ? "good" : "warn"}>{h.side}</Badge>
                  </td>
                  <td className="px-3 py-1.5 text-right tnum text-term-fg">{h.qty}</td>
                  <td className="px-3 py-1.5 text-right tnum text-term-dim">{h.net_exposure_before}</td>
                  <td className="px-3 py-1.5 text-right tnum text-term-dim">{h.threshold_lots}</td>
                  <td className="px-3 py-1.5 text-term-dim">{h.reason}</td>
                  <td className="px-3 py-1.5 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => approve.mutate(h.id)}
                        disabled={approve.isPending}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => reject.mutate(h.id)}
                        disabled={reject.isPending}
                      >
                        Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function RecentHedges({
  rows,
  loading,
}: {
  rows: Awaited<ReturnType<typeof dealingDesk.recentHedges>>;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <span className="text-sm font-medium">Recent hedges</span>
      </CardHeader>
      <CardBody className="p-0">
        {loading ? (
          <div className="p-3 text-sm text-term-mute">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No hedges yet" description="Once exposure crosses threshold, hedges will appear here." />
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-term-elev text-term-mute">
              <tr>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Time</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Symbol</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Side</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Qty</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Mode</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((h) => (
                <tr key={h.id} className="border-t border-term-border hover:bg-term-elev">
                  <td className="px-3 py-1.5 tnum text-term-dim">
                    {new Date(h.created_at).toLocaleTimeString()}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-term-fg">{h.symbol}</td>
                  <td className="px-3 py-1.5">
                    <Badge tone={h.side === "BUY" ? "good" : "warn"}>{h.side}</Badge>
                  </td>
                  <td className="px-3 py-1.5 text-right tnum text-term-fg">{h.qty}</td>
                  <td className="px-3 py-1.5">
                    <Badge tone={h.mode === "AUTO" ? "good" : "warn"}>{h.mode}</Badge>
                  </td>
                  <td className="px-3 py-1.5">
                    <Badge tone={statusTone(h.status)}>{h.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function statusTone(s: string): "good" | "warn" | "neutral" | "bad" {
  switch (s) {
    case "FILLED":
    case "APPROVED":
    case "SUBMITTED":
      return "good";
    case "PENDING":
      return "warn";
    case "REJECTED":
    case "CANCELED":
      return "bad";
    default:
      return "neutral";
  }
}

function LpHealth({
  rows,
  loading,
}: {
  rows: Awaited<ReturnType<typeof dealingDesk.lpHealth>>;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <span className="text-sm font-medium">LP health</span>
      </CardHeader>
      <CardBody className="p-0">
        {loading ? (
          <div className="p-3 text-sm text-term-mute">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No LPs configured" description="Add LP sessions in Admin → LP sessions." />
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-term-elev text-term-mute">
              <tr>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">LP</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">In seq</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Out seq</th>
                <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Reject %</th>
                <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Last error</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} className="border-t border-term-border hover:bg-term-elev">
                  <td className="px-3 py-1.5 font-mono text-term-fg">{r.name}</td>
                  <td className="px-3 py-1.5">
                    <Badge tone={r.connected ? "good" : "bad"}>
                      {r.connected ? "CONNECTED" : "DOWN"}
                    </Badge>
                  </td>
                  <td className="px-3 py-1.5 text-right tnum text-term-dim">{r.in_seq}</td>
                  <td className="px-3 py-1.5 text-right tnum text-term-dim">{r.out_seq}</td>
                  <td className="px-3 py-1.5 text-right tnum text-term-dim">
                    {r.reject_rate_pct == null ? "—" : Number(r.reject_rate_pct).toFixed(2)}
                  </td>
                  <td className="px-3 py-1.5 text-term-dim">{r.last_error ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}
